import ApplicationController from '../editor/application_controller';
import { createRequest } from '../helpers/createRequest';
import { htmlToElement, insertAfter, insertBefore } from '../helpers/dom';

export default class extends ApplicationController {
  static targets = ["addButton", "inlineForm", "titleInput", "iconInput", "iconPreview", "editTitleInput", "editIconInput", "editIconPreview", "editIconLabel"]

  connect() {
    if (this.hasInlineFormTarget) {
      this.hideForm();
    }
  }

  showForm(event) {
    event.preventDefault();
    this.addButtonTarget.classList.add('hidden');
    this.inlineFormTarget.classList.remove('hidden');
    this.titleInputTarget.focus();
  }

  hideForm() {
    this.addButtonTarget.classList.remove('hidden');
    this.inlineFormTarget.classList.add('hidden');
    this.titleInputTarget.value = '';
    this.resetIconUpload();
  }

  cancel(event) {
    event.preventDefault();
    this.hideForm();
  }

  async saveDesignStage(event) {
    event.preventDefault();

    const title = this.titleInputTarget.value.trim();
    if (!title) {
      this.titleInputTarget.focus();
      return;
    }

    const formData = new FormData();
    formData.append('name', title);

    // Add icon if selected
    if (this.hasIconInputTarget && this.iconInputTarget.files[0]) {
      formData.append('icon', this.iconInputTarget.files[0]);
    }

    // Get studio ID from the data attribute on the controller element
    const studioId = this.element.dataset["x1-SectionsStudioIdValue"];

    if (!studioId) {
      console.error('Studio ID not found');
      return;
    }

    try {
      const response = await fetch(createRequest(`/x1/studios/${studioId}/sections`, 'POST', formData));
      const html = await response.text();

      // Append the new design stage to the list
      const stagesList = document.getElementById('design-stages-list');
      const newStage = htmlToElement(html);
      
      // Add border-b to the previous last item if there is one
      if (stagesList.children.length > 0) {
        const lastItem = stagesList.lastElementChild;
        lastItem.classList.add('border-b', 'border-lightest-gray');
      }

      stagesList.appendChild(newStage);

      // Reset form
      this.hideForm();
    } catch (error) {
      console.error('Error saving design stage:', error);
    }
  }

  resetIconUpload() {
    if (this.hasIconPreviewTarget) {
      this.iconPreviewTarget.style.backgroundImage = '';
    }
    if (this.hasIconInputTarget) {
      this.iconInputTarget.value = '';
    }
  }

  previewIcon(event) {
    const file = event.target.files[0];
    if (file && this.hasIconPreviewTarget) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.iconPreviewTarget.style.backgroundImage = `url(${e.target.result})`;
      };
      reader.readAsDataURL(file);
    }
  }
  
  previewEditIcon(event) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const iconPreview = event.target.parentElement.querySelector('[data-target="x1--sections.editIconPreview"]');
        iconPreview.style.backgroundImage = `url(${e.target.result})`;
        const placeholder = iconPreview.querySelector('.edit-icon-placeholder');
        if (placeholder) {
          placeholder.style.display = 'none';
        }
      };
      reader.readAsDataURL(file);
    }
  }

  addSection(event) {
    const { studioId, tabId } = event.target.dataset;
    const input = document.querySelector('#new-section')
      .content
      .cloneNode(true);
    const sections = document.querySelector('.tabs');
    const lastSection = document.querySelector(`#section-${tabId}`);
    if (lastSection) {
      insertAfter(input, lastSection);
    } else {
      sections.appendChild(input);
    }
    const newInput = sections.querySelector('input');
    newInput.dataset.studioId = studioId;
    newInput.dataset.afterTabId = tabId;
    newInput.focus();
    this.hideDropdowns();
  }

  saveSection(event) {
    const { studioId, afterTabId } = event.target.dataset;
    const formData = new FormData();
    formData.append('name', event.target.value);
    formData.append('afterTabId', afterTabId);
    fetch(createRequest(`/x1/studios/${studioId}/sections`, 'POST', formData))
      .then((response) => response.text())
      .then((html) => event.target.parentElement.replaceWith(htmlToElement(html)));
  }

  cancelAddSection(event) {
    event.target.closest('div').remove();
  }

  renameSection(event) {
    const { studioId, tabId } = event.target.dataset;
    const input = document.querySelector('#rename-section').content.cloneNode(true);
    const section = document.querySelector(`#section-${tabId}`);
    const contentDiv = section.querySelector('.section-content');
    const link = contentDiv.querySelector('a');
    const title = link.querySelector('.text-base').innerText;
    
    // Add editing class to disable hover effects
    section.classList.add('editing');
    
    // Hide the original content
    contentDiv.classList.add('hidden');
    
    // Insert the edit form
    insertBefore(input, contentDiv);
    
    // Set up the new input
    const newInputDiv = section.querySelector('[data-controller="x1--sections"]');
    const titleInput = newInputDiv.querySelector('[data-target="x1--sections.editTitleInput"]');
    const iconInput = newInputDiv.querySelector('[data-target="x1--sections.editIconInput"]');
    const iconLabel = newInputDiv.querySelector('[data-target="x1--sections.editIconLabel"]');
    const iconPreview = newInputDiv.querySelector('[data-target="x1--sections.editIconPreview"]');
    
    // Create unique ID for this specific file input
    const uniqueId = `section-icon-input-${tabId}`;
    iconInput.id = uniqueId;
    iconLabel.setAttribute('for', uniqueId);
    
    titleInput.dataset.tabId = tabId;
    titleInput.dataset.studioId = studioId;
    titleInput.value = title;
    
    iconInput.dataset.tabId = tabId;
    iconInput.dataset.studioId = studioId;
    
    // Set existing icon if present
    const existingIcon = link.querySelector('img');
    if (existingIcon) {
      iconPreview.style.backgroundImage = `url(${existingIcon.src})`;
      iconPreview.querySelector('.edit-icon-placeholder').style.display = 'none';
    }
    
    titleInput.focus();
    this.hideDropdowns();
  }

  updateSection(event) {
    const editDiv = event.target.closest('[data-controller="x1--sections"]');
    const titleInput = editDiv.querySelector('[data-target="x1--sections.editTitleInput"]');
    const iconInput = editDiv.querySelector('[data-target="x1--sections.editIconInput"]');
    
    const { studioId, tabId } = titleInput.dataset;
    const formData = new FormData();
    formData.append('name', titleInput.value);
    
    // Add icon if selected
    if (iconInput && iconInput.files[0]) {
      formData.append('icon', iconInput.files[0]);
    }
    
    fetch(createRequest(`/x1/studios/${studioId}/sections/${tabId}`, 'POST', formData))
      .then((response) => response.text())
      .then((html) => {
        // Replace the entire section with the updated HTML from server
        const section = document.querySelector(`#section-${tabId}`);
        section.classList.remove('editing');
        section.outerHTML = html;
        this.hideDropdowns();
      })
      .catch(error => {
        console.error('Error updating section:', error);
        // Show the original content again on error
        const section = document.querySelector(`#section-${tabId}`);
        section.classList.remove('editing');
        const contentDiv = section.querySelector('.section-content');
        contentDiv.classList.remove('hidden');
        editDiv.remove();
      });
  }

  cancelEditSection(event) {
    const editDiv = event.currentTarget.closest('[data-controller="x1--sections"]');
    const section = editDiv.closest('li');
    const contentDiv = section.querySelector('.section-content');
    
    // Remove editing class to re-enable hover effects
    section.classList.remove('editing');
    
    // Show the original content
    contentDiv.classList.remove('hidden');
    
    // Remove the edit form
    editDiv.remove();
  }

  hideDropdowns() {
    document.querySelectorAll('[data-target="x1--dropdown.menu"]').forEach((el) => el.classList.add('hidden'));
  }
}
